  // src/App.jsx
  import React, { useEffect, useState } from 'react';
  import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
  import { ChakraProvider, extendTheme, Box, useToast } from '@chakra-ui/react';
  import { SpeedInsights } from "@vercel/speed-insights/react";
  import { Analytics } from '@vercel/analytics/react'
  import { AuthProvider } from './context/AuthContext';
  import { CartProvider } from './context/CartContext';
  import { CurrentEventProvider } from './context/CurrentEventContext';
  import Home from './components/Home';
  import Editor from './components/Editor';
  import LoginPage from './components/LoginPage'; 
  import ImageUploader from './components/ImageUploader';
  import TemplateSelector from './components/TemplateSelector';
  import CustomizationPage from './components/CustomizationPage';
  import RegistrationPage from './components/RegistrationPage';
  import UserDashboard from './components/UserDashboard';
  import Dashboard from './components/Dashboard';
  import { EventForm } from './components/EventForm';
  import { EventTemplates } from './components/EventTemplates';
  import EventDashboard from './components/EventDashboard';
  import UserImagesUpload from './components/UserImagesUpload';
  import CRMDashboard from './components/crm/CRMDashboard';
  import AdminStaffCreator from './components/AdminStaffCreator';
  import UserManagement from './components/UserManagement';
  import TemplateTestPage from './components/TemplateTestPage';
  import supabase from './utils/supabaseUtils';
  import { getOrCreateAppUser } from './utils/supabaseUtils';
  import ImageApprovalPage from './components/ImageApprovalPage';
  import Layout from './components/Layout';
  import Contact from './components/Contact';
  import Pricing from './components/Pricing';
  import CheckoutPage from './components/CheckoutPage';
  import OrderSuccessPage from './components/OrderSuccessPage';
  import ApprovalsList from './components/ApprovalsList';
  import ErrorBoundary from './components/ErrorBoundary';
  import VerifyAuth from './components/VerifyAuth';
  import PackagesPage from './components/PackagesPage';
  import ProductsPage from './commerce/ProductsPage';
  import MyEvents from './pages/MyEvents';
  import SimpleSignupForm from './components/SimpleSignupForm';
  import EventSignupList from './components/EventSignupList';
  import { DebugEventOrders } from './components/DebugEventOrders';
  import MagicLinkHandler from './components/MagicLinkHandler';
  import './styles/theme.css';
  import AdminRoute from './components/AdminRoute';
  import AdminBulkSignup from './components/AdminBulkSignup';
  import LocalSignup from './components/LocalSignup';
  import TermsOfService from './components/TermsOfService';
  import PrivacyPolicy from './components/PrivacyPolicy';
  import OptInPolicy from './components/OptInPolicy';
  import EnhancedTemplateSelector from './components/EnhancedTemplateSelector';
  import SmartDesignTester from './components/SmartDesignTester';
  import DynamicGenerationDemo from './components/DynamicGenerationDemo';
import DynamicCustomizationPage from './components/DynamicCustomizationPage';
  
  // Alias the dashboard components for clarity
  const OldDashboardComponent = UserDashboard;
  const MainDashboardComponent = Dashboard;
  
  // Create a custom theme with our 2025 minimal design style
  const theme = extendTheme({
    colors: {
      brand: {
        // Primary brand color (warm orange)
        500: '#D97C2B',
        600: '#C16A20', // darker for hover states
      },
      accent: {
        // Secondary brand color (coastal green)
        500: '#5A9A7E',
        600: '#4B8C70', // darker for hover states
      },
      text: {
        primary: '#2A2A2A', // Rich Gray
        secondary: '#696969', // Medium Gray for secondary text
        light: '#8A8A8A', // Light Gray for tertiary text
      },
      neutral: {
        100: '#FDFDFC', // Lightest background
        200: '#F7F7F7', // Light background
        300: '#EEEEEE', // Borders
        400: '#C3C3C3', // Soft Neutral
      },
      bg: {
        primary: '#FDFDFC',
        secondary: '#F7F7F7',
      }
    },
    fonts: {
      body: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
      heading: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
    },
    components: {
      Button: {
        baseStyle: {
          fontWeight: "300",
          borderRadius: "8px",
          letterSpacing: "0.01em",
        },
        variants: {
          solid: {
            bg: "brand.500",
            color: "white",
            _hover: {
              bg: "brand.600",
              boxShadow: "0 4px 12px rgba(217, 124, 43, 0.2)",
              transform: "translateY(-2px)",
            },
          },
          secondary: {
            bg: "accent.500",
            color: "white",
            _hover: {
              bg: "accent.600",
              boxShadow: "0 4px 12px rgba(90, 154, 126, 0.2)",
              transform: "translateY(-2px)",
            },
          },
          outline: {
            borderColor: "brand.500",
            color: "brand.500",
            borderWidth: "1px",
            _hover: {
              bg: "rgba(217, 124, 43, 0.05)",
              borderColor: "brand.600",
            },
          },
          ghost: {
            color: "text.primary",
            _hover: {
              bg: "neutral.200",
            },
          },
          neutral: {
            bg: "neutral.400",
            color: "text.primary",
            _hover: {
              bg: "#B1B1B1",
              transform: "translateY(-2px)",
            },
          }
        },
      },
      Input: {
        baseStyle: {
          field: {
            borderRadius: "8px",
            fontWeight: "300",
          },
        },
        variants: {
          outline: {
            field: {
              borderColor: "rgba(0,0,0,0.08)",
              _focus: {
                borderColor: "brand.500",
                boxShadow: "0 0 0 3px rgba(217, 124, 43, 0.1)",
              },
            },
          },
        },
      },
      Card: {
        baseStyle: {
          container: {
            borderRadius: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            bg: "white",
            border: "1px solid rgba(0,0,0,0.03)",
            transition: "all 0.2s ease-in-out",
            _hover: {
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              transform: "translateY(-3px)",
            }
          },
        },
      },
      Heading: {
        baseStyle: {
          color: "text.primary",
          fontWeight: "300",
          letterSpacing: "-0.01em",
        },
      },
      Text: {
        baseStyle: {
          color: "text.primary",
          fontWeight: "300",
        },
      },
    },
    styles: {
      global: {
        body: {
          bg: "bg.primary",
          color: "text.primary",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: "300",
        },
      },
    },
  });
  
  // Import debug utils in development mode only
  const importDebugUtils = async () => {
    if (import.meta.env.DEV) {
      try {
        await import('./debug/debugApp');
        console.log('Debug utilities loaded in development mode');
      } catch (error) {
        console.error('Failed to load debug utilities:', error);
      }
    }
  };
  
  // Create a simple layout without sidebar for checkout
  const CheckoutLayout = ({ children }) => {
    return (
      <Box 
        as="main" 
        height="100vh" 
        overflow="auto" 
        width="100%" 
        position="relative"
        pt={{ base: '60px', md: '80px' }} // Add padding to the top to avoid content being hidden by the close button
      >
        {children}
      </Box>
    );
  };
  
  function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
  
    // Chakra toast for lightweight notifications
    const toast = useToast();
  
    const nav = useNavigate ? null : undefined; // placeholder, actual navigate inside later
  
    // Load debug utils on component mount
    useEffect(() => {
      importDebugUtils();
    }, []);
  
    useEffect(() => {
      const checkSession = async () => {
        const { data, error } = await supabase.auth.getSession();
        if (data.session) {
          setIsAuthenticated(true);
          const { user } = data.session;
          setUserInfo({
            firstName: user.user_metadata.given_name || '',
            lastName: user.user_metadata.family_name || '',
            email: user.email || '',
          });
          setSession(data.session);
          
          // Log the user login
          supabase.auth.getUser().then(({ data }) => {
            if (data && data.user) {
              // Log user login via direct function call
              fetch(`https://api.ipify.org?format=json`)
                .then(res => res.json())
                .then(ipData => {
                  supabase.rpc('log_user_login', { 
                    p_user_id: data.user.id,
                    p_ip_address: ipData.ip || 'unknown',
                    p_user_agent: navigator.userAgent || 'unknown'
                  })
                  .then(result => {
                    // Successfully logged login
                    console.log('Login logged successfully in checkSession');
                  })
                  .catch(err => {
                    console.warn('Failed to log user login:', err);
                  });
                })
                .catch(() => {
                  // If getting IP fails, still try to log with unknown IP
                  supabase.rpc('log_user_login', { 
                    p_user_id: data.user.id,
                    p_ip_address: 'unknown',
                    p_user_agent: navigator.userAgent || 'unknown'
                  })
                  .then(result => {
                    // Successfully logged login
                    console.log('Login logged successfully in checkSession with unknown IP');
                  })
                  .catch(err => {
                    console.warn('Failed to log user login:', err);
                  });
                });
            }
          }).catch(err => {
            console.warn('Failed to get user for login log:', err);
          });
        } else {
          console.log('No session found during initial check');
        }
        setLoading(false);
      };
  
      checkSession();
  
      const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('Auth state change event:', event, 'Session:', !!session);
        
        if (session) {
          setIsAuthenticated(true);
          setUserInfo({
            firstName: session.user.user_metadata.given_name || '',
            lastName: session.user.user_metadata.family_name || '',
            email: session.user.email || '',
          });
          setSession(session);
          
          // Log login on sign in event
          if (event === 'SIGNED_IN') {
            fetch(`https://api.ipify.org?format=json`)
              .then(res => res.json())
              .then(ipData => {
                supabase.rpc('log_user_login', { 
                  p_user_id: session.user.id,
                  p_ip_address: ipData.ip || 'unknown',
                  p_user_agent: navigator.userAgent || 'unknown'
                })
                .then(result => {
                  // Successfully logged login
                  console.log('Login logged successfully');
                })
                .catch(err => {
                  console.warn('Failed to log user login on sign in:', err);
                });
              })
              .catch(() => {
                // If getting IP fails, still try to log with unknown IP
                supabase.rpc('log_user_login', { 
                  p_user_id: session.user.id,
                  p_ip_address: 'unknown',
                  p_user_agent: navigator.userAgent || 'unknown'
                })
                .then(result => {
                  // Successfully logged login
                  console.log('Login logged successfully with unknown IP');
                })
                .catch(err => {
                  console.warn('Failed to log user login on sign in:', err);
                });
              });
          }
        } else {
          console.log('Auth state change: No session, setting unauthenticated state');
          setIsAuthenticated(false);
          setUserInfo(null);
          setSession(null);
        }
        
        // Force update loading state on any auth change to ensure UI is responsive
        setLoading(false);
      });
  
      // Listen for custom auth state force update events (from MagicLinkHandler)
      const handleAuthForceUpdate = async (event) => {
        console.log('Received auth force update event');
        const { session } = event.detail;
        
        if (session) {
          setIsAuthenticated(true);
          setUserInfo({
            firstName: session.user.user_metadata.given_name || '',
            lastName: session.user.user_metadata.family_name || '',
            email: session.user.email || '',
          });
          setSession(session);
          setLoading(false);
          console.log('Auth state force updated successfully');
        }
      };
      
      window.addEventListener('authStateForceUpdate', handleAuthForceUpdate);
  
      return () => {
        authListener.subscription.unsubscribe();
        window.removeEventListener('authStateForceUpdate', handleAuthForceUpdate);
      };
    }, []);
  
    // Redirect authenticated users who land on home page → /dashboard
    useEffect(() => {
      if (isAuthenticated && window.location.pathname === '/') {
        window.location.replace('/dashboard');
      }
    }, [isAuthenticated]);
  
    if (loading) return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#FDFDFC',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: '3px solid #EEEEEE',
          borderTopColor: '#D97C2B',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }} />
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: '16px',
          fontWeight: '500',
          color: '#2A2A2A',
          margin: 0
        }}>Loading...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  
    return (
      <ChakraProvider theme={theme}>
        <ErrorBoundary>
          <AuthProvider>
            <CartProvider>
              <BrowserRouter>
                <MagicLinkHandler />
                <CurrentEventProvider>
                  <SpeedInsights />
                  <Analytics />
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home isAuthenticated={isAuthenticated} userInfo={userInfo} />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} />} />
                    <Route path="/register" element={<RegistrationPage />} />
                    <Route path="/signup" element={<EventSignupList />} />
                    <Route path="/signup/:eventId" element={<SimpleSignupForm />} />
                    <Route path="/approve/:approvalId" element={<ImageApprovalPage />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/opt-in-policy" element={<OptInPolicy />} />
                    {/* Magic link verification route – public */}
                    <Route path="/auth/verify" element={<VerifyAuth />} />
                    <Route path="/local-signup" element={<LocalSignup />} />
                    
                    {/* Protected routes with Layout */}
                    <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
                      <Route path="/upload" element={
                        <Layout>
                          <ImageUploader onUploadComplete={(url) => console.log(url)} />
                        </Layout>
                      } />
                      <Route path="/templates" element={
                        <Layout>
                          <TemplateSelector showHowItWorks={false} />
                        </Layout>
                      } />
                      <Route path="/templates-enhanced" element={
                        <Layout>
                          <EnhancedTemplateSelector />
                        </Layout>
                      } />
                      <Route path="/test-smart-design" element={
                        <Layout>
                          <SmartDesignTester user={session?.user} />
                        </Layout>
                      } />
                      <Route path="/test-dynamic-generation" element={
                        <Layout>
                          <DynamicGenerationDemo user={session?.user} />
                        </Layout>
                      } />
                      <Route path="/dynamic-editor/:sessionId" element={
                        <Layout>
                          <DynamicCustomizationPage />
                        </Layout>
                      } />
                      <Route path="/editor" element={
                        <Layout>
                          <Editor />
                        </Layout>
                      } />
                      <Route path="/customize/:templateId" element={
                        <Layout>
                          <CustomizationPage />
                        </Layout>
                      } />
                      <Route path="/old-dashboard" element={
                        <Layout>
                          <OldDashboardComponent />
                        </Layout>
                      } />
                      <Route path="/dashboard" element={
                        <Layout>
                          <MainDashboardComponent activeSection="dashboard" />
                        </Layout>
                      } />
                      <Route path="/designs" element={
                        <Layout>
                          <MainDashboardComponent activeSection="designs" />
                        </Layout>
                      } />
                      <Route path="/images" element={
                        <Layout>
                          <MainDashboardComponent activeSection="images" />
                        </Layout>
                      } />
                      <Route path="/orders" element={
                        <Layout>
                          <MainDashboardComponent activeSection="orders" />
                        </Layout>
                      } />
                      <Route path="/admin" element={
                        <Layout>
                          <MainDashboardComponent activeSection="admin" />
                        </Layout>
                      } />
                      <Route path="/settings" element={
                        <Layout>
                          <MainDashboardComponent activeSection="settings" />
                        </Layout>
                      } />
                      <Route path="/dashboard/events/new" element={
                        <Layout>
                          <EventForm />
                        </Layout>
                      } />
                      <Route path="/dashboard/events/:eventId/edit" element={
                        <Layout>
                          <EventForm />
                        </Layout>
                      } />
                      <Route path="/events/:eventId/templates" element={
                        <Layout>
                          <EventTemplates />
                        </Layout>
                      } />
                      <Route path="/dashboard/events/:eventId" element={
                        <Layout>
                          <EventDashboard />
                        </Layout>
                      } />
                      <Route path="/dashboard/events/:eventId/users/:userId/images" element={
                        <Layout>
                          <UserImagesUpload />
                        </Layout>
                      } />
                      <Route path="/dashboard/crm" element={
                        <Layout>
                          <CRMDashboard />
                        </Layout>
                      } />
                      <Route path="/dashboard/staff/new" element={
                        <Layout>
                          <AdminStaffCreator />
                        </Layout>
                      } />
                      <Route path="/dashboard/users" element={
                        <Layout>
                          <UserManagement />
                        </Layout>
                      } />
                      {/* Approvals route */}
                      <Route path="/dashboard/approvals" element={
                        <Layout>
                          <ApprovalsList />
                        </Layout>
                      } />
                      {/* Checkout routes without sidebar */}
                      <Route path="/checkout" element={
                        <CheckoutLayout>
                          <CheckoutPage />
                        </CheckoutLayout>
                      } />
                      <Route path="/dashboard/checkout" element={
                        <CheckoutLayout>
                          <CheckoutPage />
                        </CheckoutLayout>
                      } />
                      <Route path="/order-success" element={
                        <CheckoutLayout>
                          <OrderSuccessPage />
                        </CheckoutLayout>
                      } />
                      {/* Keeping the original routes but replacing them with Navigate */}
                      <Route path="/checkout-old" element={
                        <Layout>
                          <CheckoutPage />
                        </Layout>
                      } />
                      <Route path="/dashboard/checkout-old" element={
                        <Layout>
                          <CheckoutPage />
                        </Layout>
                      } />
                      <Route path="/order-success-old" element={
                        <Layout>
                          <OrderSuccessPage />
                        </Layout>
                      } />
                      
                      {/* Template testing route */}
                      <Route path="/test-templates" element={
                        <Layout>
                          <TemplateTestPage />
                        </Layout>
                      } />
                      <Route path="/packages" element={
                        <Layout>
                          <PackagesPage />
                        </Layout>
                      } />
                      <Route path="/products" element={
                        <Layout>
                          <ProductsPage />
                        </Layout>
                      } />
                      {/* new event-picker route */}
                      <Route path="/my-events" element={
                        <Layout>
                          <MyEvents />
                        </Layout>
                      } />
                      {/* Debug route for testing event_orders */}
                      <Route path="/debug/event-orders" element={
                        <Layout>
                          <DebugEventOrders />
                        </Layout>
                      } />
                      {/* Bulk signup admin route */}
                      <Route path="/dashboard/events/:eventId/bulk-signup" element={
                        <AdminRoute>
                          <Layout>
                            <AdminBulkSignup />
                          </Layout>
                        </AdminRoute>
                      } />
                    </Route>
                    
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </CurrentEventProvider>
              </BrowserRouter>
            </CartProvider>
          </AuthProvider>
        </ErrorBoundary>
      </ChakraProvider>
    );
  }
  
  // Add this component for protected route handling
  const ProtectedRoute = ({ isAuthenticated }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    return <Outlet />;
  };
  
  export default App;